import { useLayoutEffect, useState, type ReactNode } from 'react'
import { changeAppLanguage } from './config'
import {
  LanguageContext,
  LANGUAGE_CONFIGS,
  resolveLanguage,
  type Language,
} from './LanguageContext'

const STORAGE_KEY = 'qpoint-language'

const VALID_LANGUAGES: Language[] = ['en', 'ar', 'es', 'fr', 'zh', 'system']

function getSavedLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && (VALID_LANGUAGES as string[]).includes(stored))
      return stored as Language
  } catch {
    // ignore
  }
  return 'en'
}

function applyDirection(lang: Language) {
  const resolved = resolveLanguage(lang)
  const { dir, locale } = LANGUAGE_CONFIGS[resolved]
  document.documentElement.dir = dir
  document.documentElement.lang = locale
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(getSavedLanguage)

  useLayoutEffect(() => {
    const resolved = resolveLanguage(language)
    applyDirection(language)
    void changeAppLanguage(resolved)
  }, [language])

  const setLanguage = (lang: Language) => {
    setLanguageState(lang)
    try {
      localStorage.setItem(STORAGE_KEY, lang)
    } catch {
      // ignore
    }
  }

  const resolved = resolveLanguage(language)
  const config = LANGUAGE_CONFIGS[resolved]

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage,
        isRTL: config.dir === 'rtl',
        dir: config.dir,
      }}
    >
      {children}
    </LanguageContext.Provider>
  )
}
